import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import { Show, useNotify, useRecordContext } from "react-admin";

import { API_URL } from "../../config";
import { adminApi } from "../../dataProvider";

type Attachment = {
  name: string;
  mime: string;
  size: number;
  is_image: boolean;
  url: string;
};

type Message = {
  id: string;
  sender: "user" | "admin";
  body: string | null;
  created_at: string;
  attachment: Attachment | null;
};

type ThreadDetail = {
  id: string;
  user_phone: string | null;
  user_name: string | null;
  user_username: string | null;
  user_telegram_id: number | null;
  status: string;
  messages: Message[];
};

const POLL_MS = 5000;

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function AttachmentView({ att }: { att: Attachment }) {
  const href = API_URL + att.url;
  if (att.is_image) {
    return (
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noreferrer"
        sx={{ display: "block", mt: 0.5 }}
      >
        <Box
          component="img"
          src={href}
          alt={att.name}
          sx={{ display: "block", maxWidth: 240, maxHeight: 240, borderRadius: 1, objectFit: "contain" }}
        />
      </Box>
    );
  }
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noreferrer"
      sx={{ display: "inline-flex", alignItems: "center", gap: 1, mt: 0.5, textDecoration: "none" }}
    >
      <AttachFileIcon fontSize="small" />
      <Typography variant="body2">{att.name}</Typography>
    </Box>
  );
}

function MessageBubble({ msg }: { msg: Message }) {
  const mine = msg.sender === "admin";
  return (
    <Box sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: "70%",
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: mine ? "primary.main" : "action.hover",
          color: mine ? "primary.contrastText" : "text.primary",
        }}
      >
        {msg.attachment && <AttachmentView att={msg.attachment} />}
        {msg.body && (
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {msg.body}
          </Typography>
        )}
        <Typography variant="caption" sx={{ display: "block", textAlign: "right", opacity: 0.7, mt: 0.25 }}>
          {timeLabel(msg.created_at)}
        </Typography>
      </Paper>
    </Box>
  );
}

function Conversation() {
  const record = useRecordContext<{ id: string }>();
  const notify = useNotify();
  const threadId = record?.id;
  const [thread, setThread] = useState<ThreadDetail | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const load = useCallback(async () => {
    if (!threadId) return;
    try {
      const data = await adminApi<ThreadDetail>(`/admin/support-threads/${threadId}`);
      setThread((prev) => {
        if (!prev || prev.messages.length !== data.messages.length) scrollToBottom();
        return data;
      });
    } catch {
      /* ignore */
    }
  }, [threadId, scrollToBottom]);

  useEffect(() => {
    void load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const sendText = useCallback(async () => {
    const body = text.trim();
    if (!body || busy || !threadId) return;
    setBusy(true);
    try {
      await adminApi(`/admin/support-threads/${threadId}/messages`, { method: "POST", json: { text: body } });
      setText("");
      await load();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Ошибка", { type: "error" });
    } finally {
      setBusy(false);
    }
  }, [text, busy, threadId, load, notify]);

  const sendFile = useCallback(
    async (file: File) => {
      if (!threadId) return;
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (text.trim()) fd.append("caption", text.trim());
        const r = await fetch(`${API_URL}/admin/support-threads/${threadId}/messages/file`, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        if (!r.ok) throw new Error((await r.text().catch(() => "")) || "Не удалось отправить файл");
        setText("");
        await load();
      } catch (e) {
        notify(e instanceof Error ? e.message : "Ошибка", { type: "error" });
      } finally {
        setBusy(false);
      }
    },
    [threadId, text, load, notify],
  );

  if (!thread) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const clientLabel =
    thread.user_name ||
    thread.user_phone ||
    (thread.user_username ? `@${thread.user_username}` : "Клиент");

  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6">{clientLabel}</Typography>
        {thread.user_phone && <Chip size="small" label={thread.user_phone} />}
        {!thread.user_telegram_id && <Chip size="small" color="warning" label="Веб (нет Telegram)" />}
      </Stack>

      <Box
        ref={scrollRef}
        sx={{
          height: 440,
          overflowY: "auto",
          p: 1.5,
          borderRadius: 2,
          bgcolor: "background.default",
          border: 1,
          borderColor: "divider",
        }}
      >
        {thread.messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
            Сообщений пока нет.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {thread.messages.map((m) => (
              <MessageBubble key={m.id} msg={m} />
            ))}
          </Stack>
        )}
      </Box>

      <Stack direction="row" spacing={1} alignItems="flex-end">
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) void sendFile(f);
          }}
        />
        <IconButton onClick={() => fileRef.current?.click()} disabled={busy} aria-label="Прикрепить файл">
          <AttachFileIcon />
        </IconButton>
        <MuiTextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void sendText();
            }
          }}
          placeholder="Ответ клиенту…"
          multiline
          maxRows={5}
          fullWidth
          size="small"
        />
        <IconButton
          color="primary"
          onClick={() => void sendText()}
          disabled={busy || !text.trim()}
          aria-label="Отправить"
        >
          <SendIcon />
        </IconButton>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Клиент получит уведомление в Telegram. Картинки и PDF пересылаются прямо в чат.
      </Typography>
    </Stack>
  );
}

export function SupportShow() {
  return (
    <Show actions={false} component="div">
      <Box sx={{ p: 2 }}>
        <Conversation />
      </Box>
    </Show>
  );
}
